import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { prepareLogsDateRange } from 'infrastructure/functions';

import type { IChartSessionLogResponse } from 'infrastructure/interfaces/i-session-log';
import type { DateRange } from 'infrastructure/types';

const getUrl = (guid: string) => `core-2/patients/${guid}/chart-session/logs`;

export const CHART_SESSION_LOGS_PER_PAGE = 15;

interface IFilterProps {
  patientGuid: string;
  page: number;
  startTime?: number;
  endTime?: number;
}

const queryFn = async ({
  patientGuid,
  page,
  startTime,
  endTime,
}: IFilterProps) => {
  const url = getUrl(patientGuid);
  const { data, error } = await dataService.getList<IChartSessionLogResponse>(
    apiUrlGenerator(url, {
      page,
      items: CHART_SESSION_LOGS_PER_PAGE,
      startTime,
      endTime,
    }),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

interface IProps {
  patientGuid: string;
  page: number;
  dateRange: DateRange | undefined;
}

export const useChartSessionLogs = ({
  patientGuid,
  page,
  dateRange,
}: IProps) => {
  const queryKey = ['getChartSessionLogs', patientGuid, page, dateRange];
  const range = prepareLogsDateRange(dateRange);

  const filter: IFilterProps = {
    patientGuid,
    page,
    ...range,
  };

  const { data, isLoading, isFetching, error } = useQuery<
    IChartSessionLogResponse | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: () => queryFn(filter),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { data, isLoading, isFetching };
};
